import { useCallback, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from './Button';
import { faengtAussenklickAb } from '@/onboarding/tourSchutz';
import { FOKUS_RING } from '@/lib/fokus';
import { fehlerOhneInhalt, melde } from '@/lib/nutzung';

// Bestaetigungsdialog auf Basis von @radix-ui/react-dialog. Bewusst kein
// zusaetzliches @radix-ui/react-alert-dialog als Abhaengigkeit — der
// Unterschied ist im Wesentlichen die ARIA-Rolle, die wir hier selbst setzen.
// Genutzt fuer destruktive Aktionen (B6) und die weiche Endzustands-Sperre (B15).

export type ConfirmOptions = {
  titel: string;
  text?: string;
  bestaetigenLabel?: string;
  abbrechenLabel?: string;
  destruktiv?: boolean;
  /**
   * Tipp-Wand: Der Bestätigen-Knopf bleibt gesperrt, bis dieses Wort
   * eingetippt ist (wie „IMPORTIEREN" in der Import-App). Für Schritte, die
   * man nicht aus Versehen tun soll, die aber kein Löschen sind — ein
   * Klick ist dort zu billig, ein zweiter Dialog erzieht zum Wegklicken.
   *
   * Groß-/Kleinschreibung zählt nicht, und Umlaute dürfen als ae/oe/ue
   * getippt werden: Die Wand soll aufhalten, nicht an der Tastatur
   * scheitern lassen.
   */
  tippWort?: string;
};

/** Passt die Eingabe zur Tipp-Wand? Exportiert für den Test. */
export function tippWortPasst(eingabe: string, wort: string): boolean {
  const norm = (t: string): string =>
    t
      .trim()
      .toLocaleUpperCase('de-DE')
      .replace(/Ä/g, 'AE')
      .replace(/Ö/g, 'OE')
      .replace(/Ü/g, 'UE')
      .replace(/ß/g, 'SS');
  return norm(eingabe) !== '' && norm(eingabe) === norm(wort);
}

type OffeneAnfrage = ConfirmOptions & { resolve: (ok: boolean) => void };

/**
 * Imperatives Confirm: `const ok = await confirm({ titel: '…' })`.
 * Das zurueckgegebene `dialog`-Element muss einmal im Baum gerendert werden.
 */
export function useConfirm() {
  const [anfrage, setAnfrage] = useState<OffeneAnfrage | null>(null);
  const [getippt, setGetippt] = useState('');
  // Ref haelt den aktuellen resolve, damit ein Schliessen ueber ESC/Overlay
  // das Promise nicht haengen laesst.
  const offenRef = useRef<OffeneAnfrage | null>(null);
  offenRef.current = anfrage;

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setGetippt('');
      setAnfrage({ ...opts, resolve });
    });
  }, []);

  const schliessen = useCallback((ok: boolean): void => {
    const a = offenRef.current;
    setAnfrage(null);
    // Nutzungsmessung: welche Rückfragen werden abgebrochen? Der Titel ohne
    // Inhalt — was in Anführungszeichen steht, fällt heraus.
    if (a && !ok) melde('dialog_abgebrochen', { ort: fehlerOhneInhalt(a.titel) });
    if (a) a.resolve(ok);
  }, []);

  const dialog = (
    <Dialog.Root
      open={anfrage !== null}
      onOpenChange={(o) => {
        if (!o) schliessen(false);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-ink/30" />
        <Dialog.Content
          // Die Tour haengt am <body>: ohne diese Zeile gilt ein Klick in
          // die Sprechblase als Klick daneben und schliesst den Dialog.
          onPointerDownOutside={faengtAussenklickAb}
          onInteractOutside={faengtAussenklickAb}
          role="alertdialog"
          aria-describedby={undefined}
          // Waehrend der Einfuehrungstour ueber Overlay und Sprechblase:
          // die Rueckfrage ist eine Folge der Handlung, kein Tour-Ziel,
          // und muss beantwortbar bleiben (siehe index.css).
          data-ueber-tour=""
          className="dialog-mobil-unten fixed left-1/2 top-1/2 z-[60] w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-cream p-5 shadow-lg"
        >
          <Dialog.Title className="font-serif text-lg text-ink">
            {anfrage?.titel ?? ''}
          </Dialog.Title>
          {anfrage?.text && <p className="mt-2 text-sm text-ink-soft">{anfrage.text}</p>}
          {anfrage?.tippWort !== undefined && (
            <label className="mt-4 block text-sm text-ink-soft">
              Zum Bestätigen <span className="font-medium text-ink">{anfrage.tippWort}</span>{' '}
              eintippen:
              <input
                type="text"
                autoFocus
                autoComplete="off"
                value={getippt}
                onChange={(e) => setGetippt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tippWortPasst(getippt, anfrage.tippWort ?? '')) {
                    e.preventDefault();
                    schliessen(true);
                  }
                }}
                className={`mt-1 block min-h-[44px] w-full rounded-md border border-line bg-cream px-3 py-2 text-base text-ink ${FOKUS_RING}`}
              />
            </label>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => schliessen(false)}>
              {anfrage?.abbrechenLabel ?? 'Abbrechen'}
            </Button>
            <Button
              variant={anfrage?.destruktiv ? 'danger' : 'primary'}
              onClick={() => schliessen(true)}
              disabled={
                anfrage?.tippWort !== undefined && !tippWortPasst(getippt, anfrage.tippWort)
              }
            >
              {anfrage?.bestaetigenLabel ?? 'Bestätigen'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );

  return { confirm, dialog };
}
